import React, { useState } from 'react';
import { Layout, Menu, Button, theme, Dropdown, Avatar, Space, Typography, Modal, Breadcrumb } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  LogoutOutlined,
  SettingOutlined,
  UserOutlined,
  HomeOutlined,
} from '@ant-design/icons';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getNavigationByRole } from './navigationConfig';
import Dashboard from '../../pages/Dashboard';
import UsersPage from '../../pages/UsersPage';
import VouchersPage from '../../pages/VouchersPage';
import MiniQuizPage from '../../pages/quizzes/MiniQuizPage';
import EventsPage from '../../pages/EventsPage';
import QRDisplayPage from '../../pages/QRDisplayPage';
import type { MenuProps } from 'antd';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

const MainLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  // Get navigation items based on user role
  const userNavigationItems = getNavigationByRole(user?.role || 'admin');

  // Generate breadcrumb items based on current path
  const generateBreadcrumbs = () => {
    const pathSnippets = location.pathname.split('/').filter(i => i);
    const breadcrumbItems: Array<{ href?: string; title: React.ReactNode }> = [
      {
        href: '/',
        title: <HomeOutlined />,
      },
    ];

    pathSnippets.forEach((snippet, index) => {
      const url = `/${pathSnippets.slice(0, index + 1).join('/')}`;
      const isLast = index === pathSnippets.length - 1;
      
      if (isLast) {
        breadcrumbItems.push({
          title: <span>{snippet.charAt(0).toUpperCase() + snippet.slice(1)}</span>,
        });
      } else {
        breadcrumbItems.push({
          href: url,
          title: <span>{snippet.charAt(0).toUpperCase() + snippet.slice(1)}</span>,
        });
      }
    });

    return breadcrumbItems;
  };

  const handleLogout = () => {
    Modal.confirm({
      title: 'Confirm Logout',
      content: 'Are you sure you want to logout?',
      onOk: () => {
        logout();
        navigate('/login');
      },
    });
  };

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
      onClick: () => navigate('/profile'),
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Settings',
      onClick: () => navigate('/settings'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: handleLogout,
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header className="header" style={{ 
        position: 'fixed',
        zIndex: 1,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        background: colorBgContainer,
        borderBottom: '1px solid #f0f0f0',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      }}>
        <div className="logo" style={{
          width: collapsed ? '80px' : '200px',
          height: '32px',
          background: 'linear-gradient(90deg, #1890ff, #722ed1)',
          borderRadius: borderRadiusLG,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontWeight: 'bold',
          fontSize: '16px',
          transition: 'all 0.2s',
          marginRight: '24px',
        }}>
          {collapsed ? 'KH' : 'KILOBA HUB'}
        </div>
        
        <Button
          type="text"
          icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          onClick={() => setCollapsed(!collapsed)}
          style={{
            fontSize: '16px',
            width: 64,
            height: 64,
          }}
        />

        <div style={{ flex: 1 }} />
        
        <Dropdown
          menu={{ items: userMenuItems }}
          placement="bottomRight"
          trigger={['click']}
        >
          <Space style={{ cursor: 'pointer', padding: '0 16px' }}>
            <Avatar icon={<UserOutlined />} />
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <Text strong>{user?.name || 'Admin User'}</Text>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {user?.role || 'Administrator'}
              </Text>
            </div>
          </Space>
        </Dropdown>
      </Header>

      <Layout style={{ marginTop: 64 }}>
        <Sider 
          width={200}
          trigger={null} 
          collapsible 
          collapsed={collapsed}
          style={{
            overflow: 'auto',
            height: 'calc(100vh - 64px)',
            position: 'fixed',
            left: 0,
            top: 64,
            bottom: 0,
            background: colorBgContainer,
            borderRight: '1px solid #f0f0f0',
          }}
        >
          <Menu
            mode="inline"
            selectedKeys={[location.pathname]}
            defaultOpenKeys={['users', 'games', 'security']}
            style={{ 
              height: '100%',
              borderRight: 0,
            }}
            items={userNavigationItems}
            onClick={({ key }) => navigate(key)}
          />
        </Sider>

        <Layout style={{ 
          marginLeft: collapsed ? 80 : 200,
          transition: 'margin-left 0.2s',
        }}>
          <Content style={{ 
            padding: '24px',
            minHeight: 'calc(100vh - 64px)',
          }}>
            <div style={{ marginBottom: 16 }}>
              <Breadcrumb items={generateBreadcrumbs()} />
            </div>
            
            <div
              style={{
                background: colorBgContainer,
                minHeight: 'calc(100vh - 140px)',
                padding: 24,
                borderRadius: borderRadiusLG,
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03), 0 1px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px rgba(0, 0, 0, 0.02)',
              }}
              className="site-layout-content"
            >
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/users" element={<UsersPage />} />
                <Route path="/events" element={<EventsPage />} />
                <Route path="/events/:eventId/qr" element={<QRDisplayPage />} />
                <Route path="/quizzes" element={<MiniQuizPage />} />
                <Route path="/vouchers" element={<VouchersPage />} />
              </Routes>
            </div>
          </Content>
        </Layout>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
